// crewbit-admin/src/lib/activityTimelineIcons.ts
//
// Pure activity_type → {Icon, tone} mapping for §InvoiceDetail timeline.
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §InvoiceDetail.D3.
//
// activity_log carries both Title-Case (e.g. 'Invoice Refunded' from
// record_stripe_invoice_refund / mark_invoice_refunded_manually) and
// snake_case (e.g. 'invoice_reminder_sent' from send_reminder,
// 'invoice_receipt_sent' from send_receipt) writers. Convention drift is
// intentional per Hard rule #9 — we lower-case + replace spaces with
// underscores at READ time so the same case-fold path matches both shapes.

import {
  FileText, Send, Eye, CheckCircle, Bell, XCircle, FileX, RotateCcw,
  AlertTriangle, Check, Edit2, Circle,
} from 'lucide-react';

export type TimelineTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface TimelineIcon {
  Icon: typeof FileText;
  tone: TimelineTone;
}

export function iconForActivityType(activityType: string): TimelineIcon {
  const normalized = activityType.toLowerCase().replace(/\s+/g, '_');
  switch (normalized) {
    case 'invoice_created':
      return { Icon: FileText, tone: 'neutral' };
    case 'invoice_emailed':
    case 'invoice_sent':
      return { Icon: Send, tone: 'info' };
    case 'invoice_viewed':
      return { Icon: Eye, tone: 'info' };
    case 'invoice_paid':
    case 'payment_recorded':
    case 'project_payment_recorded':
      return { Icon: CheckCircle, tone: 'success' };
    case 'invoice_reminder_sent':
      return { Icon: Bell, tone: 'warning' };
    case 'invoice_receipt_sent':
      return { Icon: CheckCircle, tone: 'info' };
    case 'invoice_cancelled':
    case 'cancelled':
      return { Icon: XCircle, tone: 'neutral' };
    case 'invoice_written_off':
    case 'bad_debt_marked':
      return { Icon: FileX, tone: 'neutral' };
    case 'invoice_refunded':
    case 'invoice_partially_refunded':
    case 'refund_issued':
      return { Icon: RotateCcw, tone: 'warning' };
    case 'dispute_opened':
      return { Icon: AlertTriangle, tone: 'danger' };
    case 'dispute_resolved':
      return { Icon: Check, tone: 'success' };
    case 'updated':
      return { Icon: Edit2, tone: 'neutral' };
    default:
      return { Icon: Circle, tone: 'neutral' };
  }
}
